import React, { useState } from 'react'
import styled from '@emotion/styled'
import TextField from '@mui/material/TextField'
import Button from '@mui/material/Button'
import Divider from '@mui/material/Divider'
import { useStore } from '../../Store'
import axios from 'axios'
import {
  QA_PROXY_ENDPOINT,
  PRODUCTION_ENDPOINT,
} from '../../Constants/endpoints'
import MemberValidation from './MemberValidation'
import { getCredentials } from '../../Utils'
import { useDebounce } from '../../Hooks/useDebounce'

const AircraftInfoContainer = styled('div')(({ theme }) => ({
  fontSize: '.75em',
  width: '70%',
  color: theme.palette.primary.main,
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  [theme.breakpoints.down('sm')]: {
    width: '100%',
    padding: '0 3rem',
  },
}))

const InputStyle = styled(TextField)(({ theme }) => ({
  width: '100%',
  marginBottom: '1rem',
  [theme.breakpoints.only('xs')]: {
    marginTop: '0.5rem',
  },
}))

const ButtonStyle = styled(Button)({
  width: '100%',
  marginBottom: '2rem',
  marginTop: '1rem',
})

const TAILNUMBER_SEARCH = 'tailNumber'
const ICAO_SEARCH = 'aircraftICAOType'

function AircraftInfo(props) {
  const { values, handleChange, setFieldValue, errors } = props.formikProps
  const [icaoFound, setIcaoFound] = React.useState(false)

  const [validationParameter, setValidationParameter] =
    useState(TAILNUMBER_SEARCH)

  const { setAircraft, aircraft, setLoading, setAlertMessage } = useStore(
    (state) => ({
      setAircraft: state.setAircraft,
      aircraft: state.aircraft,
      setLoading: state.setLoading,
      setAlertMessage: state.setAlertMessage,
    })
  )

  const validateTailNumber = async (tailNbr) => {
    const BASE_URL = PRODUCTION_ENDPOINT || QA_PROXY_ENDPOINT
    const url = `${BASE_URL}/aircraft/tail?tailNumber=${tailNbr}`

    let getRequest = {
      method: 'get',
      url,
      headers: {
        'Content-type': 'application/json; charset=UTF-8',
        Authorization: getCredentials(),
      },
    }

    try {
      setLoading(true)
      const response = await axios(getRequest)
      const aircraftFound = response.data

      console.log('Aircraft found: ', aircraftFound)

      if (aircraftFound) {
        setFieldValue(
          'aircraftICAOType',
          aircraftFound?.makeAndModel?.icaoTypeCode ||
            aircraftFound?.makeAndModel?.model ||
            'Not Found'
        )
        setFieldValue('mtowLbs', aircraftFound?.makeAndModel?.mtowLbs || 0)
        setFieldValue('operator', aircraftFound?.ownerName || 'Not Found')
        setAircraft(aircraftFound)
        setIcaoFound(true)
      } else {
        setValidationParameter(ICAO_SEARCH)
        setFieldValue('aircraftICAOType', '')
        setFieldValue('mtowLbs', 0)
        setFieldValue('operator', '')
        setAircraft({})
        setIcaoFound(false)
      }
    } catch (error) {
      console.log(error)
    } finally {
      setLoading(false)
    }
  }

  const validateIcaoCode = async (icaoCode) => {
    const BASE_URL = PRODUCTION_ENDPOINT || QA_PROXY_ENDPOINT
    const url = `${BASE_URL}/aircraft/list?icaoTypeCodeFilter=${icaoCode}`

    let getRequest = {
      method: 'get',
      url,
      headers: {
        'Content-type': 'application/json; charset=UTF-8',
        Authorization: getCredentials(),
      },
    }

    try {
      setLoading(true)
      const response = await axios(getRequest)
      const aircraftsFound = response.data
      const { content } = aircraftsFound
      const biggerMtowLbsAircraft = content.reduce((bigger, curr) => {
        const possibleValue = curr?.makeAndModel?.mtowLbs || 0
        const biggerValue = bigger?.makeAndModel?.mtowLbs || 0
        return possibleValue > biggerValue ? curr : bigger
      }, content[0])
      if (biggerMtowLbsAircraft) {
        setFieldValue(
          'mtowLbs',
          biggerMtowLbsAircraft?.makeAndModel?.mtowLbs || 0
        )
        setAircraft(biggerMtowLbsAircraft)
      } else {
        setAlertMessage(
          'ICAO Code not found, please reach support at +1 (786) 206.6147'
        )
      }
    } catch (error) {
      console.log(error)
    } finally {
      setLoading(false)
    }
  }

  const { onTriggerValueChange } = useDebounce({
    timer: 1500,
    functionToDebounce(...args) {
      validateTailNumber(args)
    },
  })

  const tailNumberCode = (
    <InputStyle
      size="small"
      focused
      onChange={(e) => {
        setFieldValue('tailNumber', e.target.value.toUpperCase())
        if (aircraft) {
          onTriggerValueChange(e.target.value)
        }
      }}
      id="tailNumber"
      name="tailNumber"
      label="TAIL NUMBER*"
      variant="outlined"
      value={values['tailNumber']}
    />
  )
  const icaoCode = (
    <InputStyle
      size="small"
      id="aircraftICAOType"
      name="aircraftICAOType"
      label="AIRCRAFT ICAO TYPE*"
      variant="outlined"
      value={values['aircraftICAOType']}
      onChange={(e) =>
        setFieldValue('aircraftICAOType', e.target.value.toUpperCase())
      }
      disabled={icaoFound}
    />
  )

  return (
    <AircraftInfoContainer>
      <h6 style={{ marginBottom: '1rem' }}>1.- AIRCRAFT INFORMATION</h6>
      {validationParameter === TAILNUMBER_SEARCH || aircraft ? (
        tailNumberCode
      ) : (
        <></>
      )}
      {validationParameter === ICAO_SEARCH || aircraft ? icaoCode : <></>}
      {aircraft && (
        <>
          <InputStyle
            size="small"
            id="operator"
            name="operator"
            label="OWNER NAME *"
            variant="outlined"
            value={values['operator']}
            onChange={handleChange}
            error={!!errors[`operator`]}
            helperText={errors[`operator`] && errors[`operator`]}
            // focused
            // disabled
          />
          <InputStyle
            size="small"
            id="mtowLbs"
            name="mtowLbs"
            label="MTOW (Lbs) *"
            variant="outlined"
            value={values['mtowLbs']}
            onChange={handleChange}
            // error={!!errors[`mtowLbs`]}
            // helperText={errors[`mtowLbs`] && errors[`mtowLbs`]}
            // focused
            disabled={!!aircraft?.makeAndModel?.mtowLbs || icaoFound}
          />
        </>
      )}
      <ButtonStyle
        onClick={() => {
          if (validationParameter === TAILNUMBER_SEARCH) {
            validateTailNumber(values[validationParameter])
          } else {
            validateIcaoCode(values[validationParameter])
          }
        }}
        variant="contained"
        color="primary"
        disabled={
          !values[validationParameter] || aircraft?.makeAndModel?.mtowLbs
        }
      >
        {validationParameter === TAILNUMBER_SEARCH
          ? 'Validate Tail Number'
          : 'Validate ICAO Code'}
      </ButtonStyle>
      <Divider style={{ marginBottom: '1rem' }} />
      {/* ----- AQUI VAMOS A PONER EL MENSAJE */}
      {aircraft && <MemberValidation formikProps={props.formikProps} />}
    </AircraftInfoContainer>
  )
}

export default AircraftInfo
